import React, { useEffect, useMemo, useRef, useState } from "react"
import { createPortal } from "react-dom"
import { useLocation, useNavigate } from "react-router-dom"
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline"
import { useAuthStore } from "stores"
import { ZIndex } from "constants/zIndex"
import {
  askHandhold,
  isHandholdAvailable,
} from "components/handhold/askHandhold"
import { buildAccessContext } from "./access"
import { GlobalSearchResults, HandholdSuggestion } from "./GlobalSearchResults"
import { destinations as defaultDestinations } from "./registry"
import {
  currentModuleFromPath,
  groupByModule,
  searchDestinations,
} from "./search"
import { useGlobalSearchStore } from "./store"
import type { Destination, DestinationMatch, Shell } from "./types"

const MAX_RESULTS = 20

/**
 * True when a keystroke belongs to whatever the user is typing into, rather
 * than to us. Without this the global binding fires inside every form in the
 * app — Ctrl+K also means "delete to end of line" in a text field on some
 * platforms. Our own search input is exempt, or the shortcut could not close
 * the results it just opened.
 */
export function shouldIgnoreShortcut(target: EventTarget | null): boolean {
  const element = target as HTMLElement | null
  if (!element) return false
  if (element.getAttribute?.("data-global-search") === "input") return false

  const tag = element.tagName?.toLowerCase()
  return (
    tag === "input" || tag === "textarea" || element.isContentEditable === true
  )
}

const isOpenShortcut = (event: KeyboardEvent) =>
  (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k"

const isMac = () =>
  typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform)

/** A key cap for the dialog footer. */
const Key: React.FC<{ children: React.ReactNode; label?: string }> = ({
  children,
  label,
}) => (
  <kbd
    aria-label={label}
    className="inline-flex h-5 min-w-[20px] items-center justify-center rounded border border-neutral40 bg-neutral20 px-1 font-sans text-[11px] leading-none text-neutral500 shadow-[0_1px_0_0_#dfe2e6]"
  >
    {children}
  </kbd>
)

/**
 * The search trigger in the top bar and the dialog it opens.
 *
 * The bar holds a compact pill that only looks like a field; clicking it (or
 * ⌘K / Ctrl+K, or the mobile icon) opens one centred dialog with the real
 * input. A single presentation for every entry point means there is exactly one
 * search input, and its results get the width and height to be read rather than
 * being squeezed into the action cluster.
 *
 * A controlled input in the sticky header is fine here: matching is a pure
 * function over a local array, so a keystroke costs a filter and a sort. The
 * argument against live input in the header was about per-keystroke network
 * fan-out, and this feature makes no requests at all.
 */
export const GlobalSearchField: React.FC<{
  shell: Shell
  className?: string
  /** Overridable for tests; defaults to the real registry. */
  destinations?: Destination[]
}> = ({ shell, className = "", destinations = defaultDestinations }) => {
  const isOpen = useGlobalSearchStore((state) => state.isOpen)
  const openFromField = useGlobalSearchStore((state) => state.openFromField)
  const close = useGlobalSearchStore((state) => state.close)

  const user = useAuthStore((state) => state.user)
  const navigate = useNavigate()
  const { pathname } = useLocation()

  const [query, setQuery] = useState("")
  const [activeIndex, setActiveIndex] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)

  const access = useMemo(() => buildAccessContext(user, shell), [user, shell])

  const ranked = useMemo(
    () =>
      searchDestinations({
        query,
        destinations,
        access,
        currentModule: currentModuleFromPath(pathname, destinations),
        limit: MAX_RESULTS,
      }),
    [query, destinations, access, pathname]
  )

  const groups = useMemo(() => groupByModule(ranked), [ranked])

  // Arrow keys walk the list in the order it renders, group by group, not in
  // rank order: grouping pulls lower-ranked matches up under their module.
  const flattened = useMemo(
    () => groups.flatMap((group) => group.matches),
    [groups]
  )

  /**
   * The handover sits one index past the last match, so a single arrow-key run
   * walks the results and then lands on it. Where HandHold is not configured
   * there is no extra index and the list ends at the last match, exactly as
   * before.
   */
  const handholdEnabled = isHandholdAvailable()
  const handholdIndex = flattened.length
  const lastIndex = handholdEnabled ? handholdIndex : flattened.length - 1

  // A new query invalidates the old highlight position.
  useEffect(() => setActiveIndex(0), [query])

  useEffect(() => {
    if (!isOpen) {
      setQuery("")
      return
    }
    inputRef.current?.focus()
  }, [isOpen])

  // The page behind stays put while the dialog is over it.
  useEffect(() => {
    if (!isOpen) return

    const previous = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      document.body.style.overflow = previous
    }
  }, [isOpen])

  // Global binding, live whether or not the results are showing.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (!isOpenShortcut(event)) return
      if (shouldIgnoreShortcut(event.target)) return

      event.preventDefault()
      if (isOpen) close()
      else openFromField()
    }

    window.addEventListener("keydown", onKeyDown)
    return () => window.removeEventListener("keydown", onKeyDown)
  }, [isOpen, openFromField, close])

  const go = (match: DestinationMatch, newTab = false) => {
    close()
    if (newTab) window.open(match.destination.route, "_blank")
    else navigate(match.destination.route)
  }

  /**
   * Hands what was typed to HandHold. The query is read before `close`, which
   * clears it, so the guide starts from what the user actually asked for
   * rather than from an empty string.
   */
  const handover = () => {
    const asked = query
    close()
    void askHandhold(asked)
  }

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault()
        // Clamped rather than wrapping: the ends stay predictable when holding
        // the key down.
        setActiveIndex((index) => Math.min(index + 1, lastIndex))
        break
      case "ArrowUp":
        event.preventDefault()
        setActiveIndex((index) => Math.max(index - 1, 0))
        break
      case "Enter": {
        if (handholdEnabled && activeIndex === handholdIndex) {
          event.preventDefault()
          handover()
          break
        }

        const match = flattened[activeIndex]
        if (!match) break
        event.preventDefault()
        go(match, event.metaKey || event.ctrlKey)
        break
      }
      default:
        break
    }
  }

  const portalTarget =
    typeof document === "undefined"
      ? null
      : document.getElementById("portal") ?? document.body

  const dialog =
    isOpen && portalTarget
      ? createPortal(
          <div
            className="fixed inset-0 flex justify-center bg-[#1E1E1E]/40 backdrop-blur-[2px] md:items-start md:px-4 md:pt-[6vh]"
            style={{ zIndex: ZIndex.CommandPalette }}
            onMouseDown={close}
          >
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Search"
              className="flex h-full w-full flex-col overflow-hidden bg-white md:h-auto md:max-h-[min(680px,86vh)] md:max-w-[640px] md:rounded-xl md:shadow-2xl"
              onMouseDown={(event) => event.stopPropagation()}
              onKeyDown={(event) => {
                if (event.key !== "Escape") return
                event.preventDefault()
                close()
              }}
            >
              <div className="flex items-center gap-2 p-3 md:p-4">
                <div className="flex h-12 flex-1 items-center gap-3 rounded-lg border-2 border-primary500 bg-white px-3">
                  <MagnifyingGlassIcon className="h-5 w-5 shrink-0 text-primary500" />
                  <input
                    data-global-search="input"
                    role="combobox"
                    aria-expanded
                    aria-controls={
                      handholdEnabled
                        ? "global-search-results global-search-suggestions"
                        : "global-search-results"
                    }
                    aria-activedescendant={
                      flattened.length > 0 || handholdEnabled
                        ? `global-search-option-${activeIndex}`
                        : undefined
                    }
                    aria-autocomplete="list"
                    aria-label="Search pages and features"
                    type="text"
                    placeholder="Search or jump to..."
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={onKeyDown}
                    ref={inputRef}
                    // 16px, so iOS does not zoom the page on focus.
                    className="h-full w-full border-0 bg-transparent text-base text-neutral700 outline-none placeholder:text-neutral300"
                  />
                  {query.length > 0 ? (
                    <button
                      type="button"
                      aria-label="Clear search"
                      onClick={() => {
                        setQuery("")
                        inputRef.current?.focus()
                      }}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded text-neutral300 hover:text-neutral700"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  ) : null}
                </div>
                {/* No Escape key on a phone. */}
                <button
                  type="button"
                  onClick={close}
                  className="shrink-0 px-1 text-sm font-medium text-primary500 md:hidden"
                >
                  Cancel
                </button>
              </div>

              <div
                id="global-search-results"
                // flex-auto, not flex-1: the dialog is content-height on
                // desktop, and the list should size to its rows until the cap
                // makes it scroll.
                className="min-h-0 flex-auto overflow-y-auto px-3 pb-3 md:px-4"
              >
                <GlobalSearchResults
                  groups={groups}
                  flattened={flattened}
                  activeIndex={activeIndex}
                  query={query}
                  onHover={setActiveIndex}
                  onSelect={(match) => go(match)}
                />
              </div>

              {/* Outside the scroll area, so it stays in view however long the
                  results run. */}
              {handholdEnabled ? (
                <div className="mx-3 shrink-0 border-t border-neutral40 pb-3 md:mx-4">
                  <HandholdSuggestion
                    index={handholdIndex}
                    activeIndex={activeIndex}
                    query={query}
                    onHover={setActiveIndex}
                    onSelect={handover}
                  />
                </div>
              ) : null}

              <div className="mx-3 hidden items-center justify-between border-t border-neutral40 py-3 text-xs text-neutral300 md:mx-4 md:flex">
                <span className="flex items-center gap-1.5">
                  <Key label="Enter">↵</Key>
                  <span>to select</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Key label="Down arrow">↓</Key>
                  <Key label="Up arrow">↑</Key>
                  <span>to navigate</span>
                </span>
                <span className="flex items-center gap-1.5">
                  <Key>esc</Key>
                  <span>to close</span>
                </span>
              </div>
            </div>
          </div>,
          portalTarget
        )
      : null

  return (
    <div className={`relative h-9 ${className}`}>
      {/* Looks like a field so it reads as search, but it is only the way in:
          the real input lives in the dialog. */}
      <button
        type="button"
        aria-haspopup="dialog"
        onClick={openFromField}
        className="flex h-9 w-full items-center gap-2 rounded-lg border border-neutral40 bg-white px-3 text-left text-sm text-neutral300 transition-colors duration-200 hover:border-neutral60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary500"
      >
        <MagnifyingGlassIcon className="h-4 w-4 shrink-0" />
        <span className="flex-1 truncate">Search or jump to...</span>
        <kbd className="shrink-0 rounded border border-neutral40 px-1.5 py-0.5 font-sans text-[10px] leading-none">
          {isMac() ? "⌘K" : "Ctrl K"}
        </kbd>
      </button>
      {dialog}
    </div>
  )
}
